import { format } from "date-fns";

export const convertTimeFromMs = (totalMilliseconds) => {
  if (isNaN(totalMilliseconds)) return "";
  const totalSeconds = Math.floor(totalMilliseconds / 1000);
  if (totalSeconds >= 86400) return `${Math.floor(totalSeconds / 86400)}d`;
  if (totalSeconds >= 3600) return `${Math.floor(totalSeconds / 3600)}h`;
  if (totalSeconds >= 60) return `${Math.floor(totalSeconds / 60)}m`;
  return `${totalSeconds}s`;
};

export const formatImageUrl = (image, host) => {
  if (!image) return "/Image/header/ddefault_img.svg";
  let imageUrl = image;
  if (imageUrl.startsWith("/"))
    imageUrl = imageUrl.substring(1, imageUrl.length);
  if (!imageUrl.startsWith("http")) imageUrl = `${host}${imageUrl}`;

  return imageUrl;
};

export const groupMessagesByDate = (messages) => {
  return messages.reduce((acc, cur) => {
    const date = format(new Date(cur.sent_at), "MMM d"); // Format to "Feb 21, 2025"
    if (!acc[date]) acc[date] = [];
    acc[date].push(cur);
    return acc;
  }, {});
};

export const createChatObj = (namesList, channelId, profilePic) => {
  const members = namesList.map((name) => {
    return {
      id: crypto.randomUUID(),
      profile_pic: profilePic,
      dm_name: name,
    };
  });
  return {
    channel: channelId,
    dm_info: {
      members,
    },
  };
};

export const setDefaultLocation = () => {
  if (
    localStorage.getItem("address") &&
    localStorage.getItem("lat") &&
    localStorage.getItem("lng")
  )
    return;
  localStorage.setItem("lat", "28.6139298");
  localStorage.setItem("lng", "77.2088282");
  const address = "New Delhi, Delhi, India";
  localStorage.setItem("address", JSON.stringify(address));
  window.dispatchEvent(new Event("getLocationFromLocalStorage"));
};

export const extractChatInfo = (chat) => {
  const members = chat?.dm_info?.members;
  let name =
    members?.length === 1
      ? members[0]?.dm_name
      : members.map((member) => member?.dm_name).join(", ");
  name = name.length > 25 ? name.substring(0, 25) + "..." : name;
  const image = chat?.dm_info?.members[0]?.profile_pic;
  const imageUrl = formatImageUrl(image, process.env.REACT_APP_API_BASE_URL);
  const lastMessage = chat?.dm_info?.last_msg;
  const lastMessageTime = chat?.elapsedTime;

  return {
    name,
    imageUrl,
    lastMessage,
    lastMessageTime,
  };
};

// Quiz Question Validatoor
// Quiz Question Validator - robust for different incoming shapes
export const validateQuestion = ({
  question,
  marks,
  options,
  correctAnswer, // can be index (number) or omitted if options have isCorrect flags
  optionsErrorMsg = "Please provide at least two choices.",
  type = "mcq",
}) => {
  // basic checks
  if (!question || (typeof question === "string" && question.trim() === "")) {
    return "Question text cannot be left empty!";
  }

  const parsedMarks = parseInt(marks, 10);
  if (Number.isNaN(parsedMarks) || parsedMarks <= 0) {
    return "Marks must be greater than zero!";
  }

  // options must be an array with at least 2 items for choice-based questions
  const choiceTypes = ["mcq", "checkbox", "true_false", "image_choice", "single_choice", "singlechoice"];
  if (choiceTypes.includes(type)) {
    if (!Array.isArray(options) || options.length < 2) {
      return optionsErrorMsg;
    }
  }

  // normalize options to objects: { choiceText, text, isCorrect }
  const normalizedOptions = Array.isArray(options)
    ? options.map((opt) => {
        if (typeof opt === "string") {
          return { choiceText: opt, text: opt, isCorrect: false };
        }
        return {
          choiceText: opt.choiceText ?? opt.text ?? opt.choice ?? "",
          text: opt.text ?? opt.choiceText ?? opt.choice ?? "",
          isCorrect: !!(opt.isCorrect ?? opt.is_correct ?? false),
        };
      })
    : [];

  // ensure no empty choice text
  const hasEmptyChoice = normalizedOptions.some((opt) => !opt.choiceText || opt.choiceText.trim() === "");
  if (hasEmptyChoice) {
    return "Choice text cannot be left empty!";
  }

  // Determine correct count from either correctAnswer (index) or option flags
  let correctCount = 0;
  if (typeof correctAnswer === "number" && Number.isInteger(correctAnswer)) {
    // index based correct answer (single-choice)
    if (correctAnswer < 0 || correctAnswer >= normalizedOptions.length) {
      return "Correct answer index is out of range.";
    }
    correctCount = 1;
  } else {
    // count isCorrect flags
    correctCount = normalizedOptions.reduce((cnt, opt) => (opt.isCorrect ? cnt + 1 : cnt), 0);
  }

  // Type-specific correctness rules
  switch (type) {
    case "mcq":
    case "single_choice":
    case "singlechoice":
      // require exactly one correct choice
      if (correctCount === 0 && typeof correctAnswer !== "number") {
        // if no flags and no index provided, treat as invalid
        return "Question must have exactly one correct choice!";
      }
      if (correctCount > 1) {
        return "Question must have only one correct choice!";
      }
      break;

    case "checkbox":
    case "multiple_choice":
      // require at least one correct
      if (correctCount < 1) {
        return "Please mark at least one correct choice for multiple-answer questions.";
      }
      break;

    case "true_false":
      // expect exactly two options and exactly one correct
      if (normalizedOptions.length !== 2) {
        return "True/False question must have exactly two options.";
      }
      if (correctCount !== 1 && typeof correctAnswer !== "number") {
        return "True/False question must have exactly one correct answer.";
      }
      break;

    default:
      // for other types, basic checks already done — let them pass
      break;
  }

  // If we reach here it's valid
  return null;
};



export const formatDateToLocal = (date) => {
  const localDate = new Date(date);
  const year = localDate.getFullYear();
  const month = String(localDate.getMonth() + 1).padStart(2, '0');
  const day = String(localDate.getDate()).padStart(2, '0');
  const hours = String(localDate.getHours()).padStart(2, '0');
  const minutes = String(localDate.getMinutes()).padStart(2, '0');
  const seconds = String(localDate.getSeconds()).padStart(2, '0');
  
  // Get timezone offset (+05:30)
  const offsetMinutes = localDate.getTimezoneOffset();
  const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
  const offsetMins = Math.abs(offsetMinutes) % 60;
  const timezone = `${offsetMinutes > 0 ? '-' : '+'}${String(offsetHours).padStart(2, '0')}:${String(offsetMins).padStart(2, '0')}`;

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${timezone}`;
};